import React, { useState, useRef } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';
import { useAuth } from '../../hooks/useAuth';

const VideoUploadModal = ({ 
  isOpen, 
  onClose, 
  onUpload,
  level = "overview",
  subsection = null,
  isUploading = false
}) => {
  const { user } = useAuth();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [processingVideo, setProcessingVideo] = useState(false);
  const [videoMetadata, setVideoMetadata] = useState(null);
  const fileInputRef = useRef();

  // Enhanced file validation with better error messages
  const validateVideoFile = (file) => {
    if (!file) {
      return { valid: false, error: 'No file provided' };
    }

    // Verify it's a File object and not corrupted data
    if (!(file instanceof File)) {
      return { valid: false, error: 'Invalid file object. Please try selecting the file again.' };
    }

    // Check file name
    if (!file?.name || typeof file?.name !== 'string') {
      return { valid: false, error: 'File appears to be corrupted. Please try selecting a different file.' };
    }

    // Enhanced video file type validation
    const allowedMimeTypes = [
      'video/mp4',
      'video/webm',
      'video/ogg',
      'video/avi', 
      'video/mov',
      'video/quicktime',
      'video/x-msvideo',
      'video/3gpp',
      'video/x-ms-wmv',
      'video/mp2t',
      'video/x-flv'
    ];

    const allowedExtensions = ['mp4', 'webm', 'ogg', 'avi', 'mov', 'wmv', '3gp', 'flv', 'mkv'];
    const fileExtension = file?.name?.toLowerCase()?.split('.')?.pop();

    // Check MIME type first
    const hasValidMimeType = allowedMimeTypes?.includes(file?.type);
    const hasValidExtension = allowedExtensions?.includes(fileExtension);

    if (!hasValidMimeType && !hasValidExtension) {
      return {
        valid: false,
        error: `Unsupported file format. Detected: ${file?.type || 'unknown type'} (.${fileExtension}). Please use MP4, WebM, MOV, or AVI files.`
      };
    }

    // File size validation
    const maxSize = 500 * 1024 * 1024; // 500MB
    if (file?.size > maxSize) {
      return {
        valid: false,
        error: `File size (${formatFileSize(file?.size)}) exceeds 500MB limit. Please compress your video or select a smaller file.`
      };
    }

    // Minimum size check
    if (file?.size < 1024) { // Less than 1KB
      return {
        valid: false,
        error: 'File appears to be empty or corrupted. Please select a valid video file.'
      };
    }

    return { valid: true };
  };

  const handleFileChange = (file) => {
    if (!file) return;

    console.log('File selected:', {
      name: file?.name,
      type: file?.type,
      size: file?.size,
      lastModified: file?.lastModified,
      isFile: file instanceof File
    });

    // Validate the file
    const validation = validateVideoFile(file);
    if (!validation?.valid) {
      setError(validation?.error);
      setSelectedFile(null);
      return;
    }

    setError(null);
    setSelectedFile(file);
    setVideoMetadata(null);

    // Start processing video metadata
    processVideoMetadata(file);
  };

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    const file = e?.dataTransfer?.files?.[0];
    if (file) {
      handleFileChange(file);
    }
  };

  // Enhanced video metadata extraction
  const processVideoMetadata = async (file) => {
    setProcessingVideo(true);
    
    try {
      const metadata = await extractVideoMetadata(file);
      setVideoMetadata(metadata);
      console.log('Video metadata extracted:', metadata);
    } catch (err) {
      console.warn('Could not extract video metadata:', err);
      setVideoMetadata({
        duration: 0,
        width: 0,
        height: 0,
        thumbnailBlob: null
      });
    } finally {
      setProcessingVideo(false);
    }
  };

  const extractVideoMetadata = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      video.preload = 'metadata';
      video.muted = true; // Ensure video can play without user interaction
      
      const onLoadedMetadata = () => {
        try {
          const duration = Math.floor(video.duration || 0);
          
          // Set canvas size to video dimensions
          canvas.width = video.videoWidth || 640;
          canvas.height = video.videoHeight || 480;
          
          // Seek to 25% for thumbnail, but ensure it's not 0
          const seekTime = Math.max(duration * 0.25, 1);
          video.currentTime = seekTime;
        } catch (err) {
          console.warn('Error in loadedmetadata handler:', err);
          resolve({
            duration: 0,
            width: 640,
            height: 480,
            thumbnailBlob: null
          });
          cleanup();
        }
      };
      
      const onSeeked = () => {
        try {
          // Draw video frame to canvas for thumbnail
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          // Convert canvas to blob
          canvas.toBlob((thumbnailBlob) => {
            resolve({
              duration: Math.floor(video.duration || 0),
              width: video.videoWidth || 640,
              height: video.videoHeight || 480,
              thumbnailBlob
            });
            cleanup();
          }, 'image/jpeg', 0.8);
        } catch (err) {
          console.warn('Error generating thumbnail:', err);
          resolve({
            duration: Math.floor(video.duration || 0),
            width: video.videoWidth || 640,
            height: video.videoHeight || 480,
            thumbnailBlob: null
          });
          cleanup();
        }
      };
      
      const onError = (err) => {
        console.warn('Video processing error:', err);
        resolve({
          duration: 0,
          width: 640,
          height: 480,
          thumbnailBlob: null
        });
        cleanup();
      };
      
      const cleanup = () => {
        if (video.src) {
          URL.revokeObjectURL(video.src);
        }
        video.removeEventListener('loadedmetadata', onLoadedMetadata);
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('error', onError);
      };

      video.addEventListener('loadedmetadata', onLoadedMetadata);
      video.addEventListener('seeked', onSeeked);
      video.addEventListener('error', onError);
      
      // Set timeout to prevent hanging
      setTimeout(() => {
        if (processingVideo) {
          console.warn('Video processing timeout');
          resolve({
            duration: 0,
            width: 640,
            height: 480,
            thumbnailBlob: null
          });
          cleanup();
        }
      }, 10000); // 10 second timeout
      
      try {
        video.src = URL.createObjectURL(file);
        video.load();
      } catch (err) {
        console.error('Error creating video URL:', err);
        reject(err);
        cleanup();
      }
    });
  };

  const handleUpload = async () => {
    if (!selectedFile || !user) {
      setError('Please select a video file before uploading');
      return;
    }
    
    try {
      // Prepare upload data with file and metadata
      const uploadData = {
        file: selectedFile,
        level: level,
        subsection: subsection,
        duration: videoMetadata?.duration || 0,
        width: videoMetadata?.width || 0,
        height: videoMetadata?.height || 0,
        thumbnailBlob: videoMetadata?.thumbnailBlob
      };

      console.log('Preparing upload with data:', {
        fileName: selectedFile?.name,
        fileType: selectedFile?.type,
        fileSize: selectedFile?.size,
        hasMetadata: !!videoMetadata,
        hasThumbnail: !!videoMetadata?.thumbnailBlob
      });

      // Call the upload handler
      const result = await onUpload(uploadData);
      
      if (result?.error) {
        setError(result?.error);
      } else {
        // Success - close modal after short delay
        setTimeout(() => {
          handleClose();
        }, 1000);
      }
      
    } catch (err) {
      console.error('Upload handler error:', err);
      setError(err?.message || 'Upload failed. Please try again.');
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setVideoMetadata(null);
    setError(null);
    setUploadProgress(0);
    setProcessingVideo(false);
    onClose?.();
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 KB';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i))?.toFixed(2))} ${sizes?.[i]}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl relative z-[100000]">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Icon name="Video" size={20} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Upload Video
                </h3>
                <p className="text-sm text-gray-500">
                  Upload a video for {level}{subsection ? ` - ${subsection}` : ''}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm" 
              onClick={handleClose}
              disabled={isUploading}
              iconName="X"
              className="text-gray-400 hover:text-gray-600"
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {/* File Upload Area */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Video File
            </label>
            
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : selectedFile 
                    ? 'border-green-300 bg-green-50' :'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => !isUploading && fileInputRef?.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,video/webm,video/ogg,video/avi,video/mov,video/quicktime,.mp4,.webm,.ogg,.avi,.mov,.wmv"
                onChange={handleFileSelect}
                className="hidden"
                disabled={isUploading}
              />
              
              {selectedFile ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-center space-x-3">
                    <Icon name="Video" size={24} className="text-green-600" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-green-800">
                        {selectedFile?.name}
                      </p>
                      <p className="text-xs text-green-600">
                        {formatFileSize(selectedFile?.size)} • {selectedFile?.type}
                      </p>
                    </div>
                  </div>
                  
                  {/* Video Metadata Display */}
                  {videoMetadata && (
                    <div className="text-xs text-gray-600 bg-gray-100 rounded p-2">
                      <div className="grid grid-cols-2 gap-2">
                        <span>Duration: {Math.floor(videoMetadata?.duration / 60)}:{String(videoMetadata?.duration % 60)?.padStart(2, '0')}</span>
                        <span>Resolution: {videoMetadata?.width}x{videoMetadata?.height}</span>
                      </div>
                      {videoMetadata?.thumbnailBlob && (
                        <span className="text-green-600">✓ Thumbnail generated</span>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <Icon name="Upload" size={32} className="text-gray-400 mx-auto" />
                  <div>
                    <p className="text-sm text-gray-600 font-medium">
                      Drop your video file here or click to browse
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Supports: MP4, WebM, MOV, AVI (max 500MB)
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Processing Status */}
          {processingVideo && (
            <div className="mb-6 bg-yellow-50 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <LoadingSpinner size="sm" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">Processing Video</p>
                  <p className="text-xs text-yellow-800">Extracting metadata and generating thumbnail...</p>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Error Display */}
          {error && (
            <div className="mb-6 bg-red-50 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="AlertTriangle" size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-900">Upload Error</p>
                  <p className="text-sm text-red-800 mt-1">{error}</p>
                  
                  {/* File Type Error Guidance */}
                  {(error?.includes('format') || error?.includes('type')) && (
                    <div className="mt-3 text-xs text-red-700 bg-red-100 rounded p-2">
                      <p className="font-medium mb-1">Supported formats:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>MP4 (recommended for best compatibility)</li>
                        <li>WebM (good for web playback)</li>
                        <li>MOV (QuickTime format)</li>
                        <li>AVI (Windows format)</li>
                      </ul>
                    </div>
                  )}
                  
                  {/* Size Error Guidance */}
                  {error?.includes('size') && (
                    <div className="mt-3 text-xs text-red-700 bg-red-100 rounded p-2">
                      <p className="font-medium mb-1">To reduce file size:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Compress video using HandBrake or similar tools</li>
                        <li>Reduce video resolution (e.g., 1080p → 720p)</li>
                        <li>Trim unnecessary sections</li>
                        <li>Use H.264 codec for MP4 files</li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Upload Instructions */}
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-blue-900 mb-1">Enhanced Video Processing Features:</p>
                <ul className="text-blue-800 space-y-1 text-xs">
                  <li>• Automatic thumbnail generation at 25% video duration</li>
                  <li>• Video metadata extraction (duration, resolution)</li>
                  <li>• Enhanced file validation and error detection</li>
                  <li>• Secure upload with progress tracking</li>
                  <li>• Optimized storage and analytics integration</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {selectedFile && (
                <span className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={16} className="text-green-500" />
                  <span>Ready: {selectedFile?.name} ({formatFileSize(selectedFile?.size)})</span>
                </span>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isUploading}
                className="border-gray-300 text-gray-700"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleUpload}
                disabled={!selectedFile || isUploading || !user || processingVideo}
                iconName={isUploading ? null : "Upload"}
                iconPosition="left"
                className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-400"
              >
                {isUploading ? (
                  <div className="flex items-center space-x-2">
                    <LoadingSpinner size="sm" />
                    <span>Uploading...</span>
                  </div>
                ) : processingVideo ? (
                  <div className="flex items-center space-x-2">
                    <LoadingSpinner size="sm" />
                    <span>Processing...</span>
                  </div>
                ) : (
                  'Upload Video'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoUploadModal;